import React from "react";
import {
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  InputAdornment,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";

import {
  TASK_STATUSES,
  TASK_PRIORITIES,
  TASK_CATEGORIES,
} from "../constants/taskConstants";

/**
 * Toolbar de filtros del módulo de tareas.
 * - search: filtrado client-side por título / descripción / paciente
 * - status / priority / category: enviados al backend como query params
 * - locations: lista opcional para filtrar por sede
 */
export default function TasksFilters({
  search,
  onSearchChange,
  status,
  priority,
  category,
  locationId,
  onStatusChange,
  onPriorityChange,
  onCategoryChange,
  onLocationChange,
  locations = [],
  onClear,
}) {
  const hasFilters =
    search || status || priority || category || locationId;

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", md: "center" }}
      sx={{ width: "100%" }}
    >
      <TextField
        size="small"
        placeholder="Search by title, patient or description..."
        value={search || ""}
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{ flex: 1, minWidth: 240 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => onSearchChange("")}>
                <ClearIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>Status</InputLabel>
        <Select
          label="Status"
          value={status || ""}
          onChange={(e) => onStatusChange(e.target.value || null)}
        >
          <MenuItem value="">All statuses</MenuItem>
          {TASK_STATUSES.map((s) => (
            <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>Priority</InputLabel>
        <Select
          label="Priority"
          value={priority || ""}
          onChange={(e) => onPriorityChange(e.target.value || null)}
        >
          <MenuItem value="">All priorities</MenuItem>
          {TASK_PRIORITIES.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color }} />
                <span>{p.label}</span>
              </Stack>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Category</InputLabel>
        <Select
          label="Category"
          value={category || ""}
          onChange={(e) => onCategoryChange(e.target.value || null)}
        >
          <MenuItem value="">All categories</MenuItem>
          {TASK_CATEGORIES.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {locations.length > 0 && (
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Location</InputLabel>
          <Select
            label="Location"
            value={locationId || ""}
            onChange={(e) => onLocationChange(e.target.value || null)}
          >
            <MenuItem value="">All locations</MenuItem>
            {locations.map((l) => (
              <MenuItem key={l.locationId} value={l.locationId}>
                {l.medicalCenter || l.name || `Location ${l.locationId}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {hasFilters && (
        <Tooltip title="Clear all filters">
          <IconButton onClick={onClear} size="small" sx={{ alignSelf: "center" }}>
            <FilterAltOffIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}
